import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * When any value in this array changes, the boundary clears its error state
   * and re-renders its children. Pass the current route (e.g. the pathname) so
   * navigating away from a crashed page recovers automatically instead of
   * leaving the fallback stuck on screen.
   */
  resetKeys?: unknown[];
  /**
   * Optional custom fallback. Receives the caught error and a reset callback.
   * When omitted, a self-contained default card is shown.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * App-wide error boundary. A render error in any descendant is caught here and
 * turned into a contained, recoverable fallback instead of unmounting the whole
 * React tree (which shows up as a blank white page). The fallback deliberately
 * uses no SDK/Redux/router dependencies so it can never throw on its own.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep surfacing the error in DevTools while keeping the app mounted.
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && this.resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  private resetKeysChanged(prev?: unknown[], next?: unknown[]): boolean {
    if (!prev || !next) return prev !== next;
    if (prev.length !== next.length) return true;
    return prev.some((value, i) => !Object.is(value, next[i]));
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Something went wrong on this page
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              An unexpected error occurred while rendering this view. You can try reloading the page.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
